/* eslint-disable no-nested-ternary */
import { Component, Input, OnInit } from '@angular/core';
import { AuthStoreService, Banner, ContentStoreService } from '@motivforce/mx-library-angular';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, take } from 'rxjs';
import { User } from 'src/app/core/model/user';

import { Currency } from '../../../core/model/core/currency';
import { RebateSummary } from '../../../core/model/lenovo/rebate-summary';
import { SalesTargetOption } from '../../../core/model/lenovo/sales-target-option';
import { UserStoreService } from '../../../core/store/user-store.service';

@UntilDestroy()
@Component({
  selector: 'app-banner',
  templateUrl: './banner.component.html',
  styleUrls: ['./banner.component.scss'],
})
export class BannerComponent implements OnInit {
  @Input() showDefaultPageBanner = false;

  banner: Banner;
  rebateSummary: RebateSummary | null;
  salesTargetOptions: SalesTargetOption[] = [];
  currentTargetIndex = 0;
  salesTargetFillMap = ['-90deg', '-45deg', '0deg', '45deg'];
  bannerImage = 'https://mmicdn.s3.eu-central-1.amazonaws.com/Lenovo%2BElevate/Images/Refresh/Hero_1920px_72dpi.jpg';
  bannerImagePastMembers =
    'https://mmicdn.s3.eu-central-1.amazonaws.com/Lenovo%2BElevate/Images/elevate_generic_banner_2.jpg';

  currency: Currency;
  progressStartPoint: string;
  numberOfBands = 4;
  calculatedAngle = 45;
  isPastMember = false;

  constructor(
    private userStore: UserStoreService,
    private contentStore: ContentStoreService,
    private authStore: AuthStoreService
  ) {}

  ngOnInit(): void {
    this.userStore.rebateSummaryDetails$.pipe(untilDestroyed(this), filter(Boolean)).subscribe((rebateSummaries) => {
      const summary = rebateSummaries.find((rs) => rs.isCurrentQ === true);
      if (!rebateSummaries || !summary) return;

      this.rebateSummary = summary;
      this.salesTargetOptions = summary.rebateBands;
      this.numberOfBands = this.salesTargetOptions.length;
      this.calculatedAngle = 180 / this.numberOfBands;
      this.calculateSalesTargetFillMap();
      this.currentTargetIndex = this.calculateCurrentBandIndex();
      const previousTargetIndex = Math.max(this.currentTargetIndex - 1, 0);

      if (this.rebateSummary.qtrRevLocalCurrency >= this.salesTargetOptions[this.currentTargetIndex].salesTarget) {
        this.progressStartPoint = `${this.calculatedAngle}deg`;
      } else {
        const previousTarget = this.salesTargetOptions[previousTargetIndex];
        const previousSalesTarget = this.currentTargetIndex === 0 ? 0 : previousTarget.salesTarget;
        const difference = summary.qtrRevLocalCurrency - previousSalesTarget;
        const currentBandPct =
          difference / (this.salesTargetOptions[this.currentTargetIndex].salesTarget - previousSalesTarget);

        const degrees = this.calculatedAngle * currentBandPct;
        this.progressStartPoint = `${degrees}deg`;
      }
    });

    this.authStore.isRegularUser$.pipe(untilDestroyed(this), filter(Boolean)).subscribe(() => {
      const userRole = (this.authStore.userSettings?.user as User)?.userRole;
      this.isPastMember = userRole?.name?.includes('past') ?? false;
    });

    this.userStore.currency$.pipe(untilDestroyed(this), filter(Boolean), take(1)).subscribe((currency) => {
      this.currency = currency;
    });

    this.contentStore.banner$.pipe(untilDestroyed(this), filter(Boolean)).subscribe((banner) => {
      if (!this.banner && this.showDefaultPageBanner) {
        this.banner = banner;
      }
    });
  }

  getQuarterLabel(): string {
    if (!this.rebateSummary) return '';

    const startMonth = (this.rebateSummary.quarterNumber - 1) * 3;
    const endMonth = startMonth + 2;
    const startMonthName = new Date(this.rebateSummary.quarterYear, startMonth, 1).toLocaleString('default', {
      month: 'short',
    });
    const endMonthName = new Date(this.rebateSummary.quarterYear, endMonth, 1).toLocaleString('default', {
      month: 'short',
    });
    return `${startMonthName}-${endMonthName} ${this.rebateSummary.yrQtr}`;
  }

  calculateSalesTargetFillMap() {
    this.salesTargetFillMap = Array.from(
      { length: this.numberOfBands },
      (_, i) => `${-90 + i * this.calculatedAngle}deg`
    );
  }

  getProgressStartPoint(): string {
    if (!this.rebateSummary) return this.salesTargetFillMap[0];

    return this.salesTargetFillMap[this.currentTargetIndex];
  }

  getProgressFillEnd(): string {
    return `${(this.currentTargetIndex || 0) * this.calculatedAngle}deg`;
  }

  private calculateCurrentBandIndex(): number {
    if (this.rebateSummary) {
      if (this.rebateSummary.qtrRevLocalCurrency < 0) return 0;

      const currentBandIndex = this.salesTargetOptions.findIndex(
        (b) => this.rebateSummary!.qtrRevLocalCurrency < b.salesTarget
      );

      return currentBandIndex === -1 ? this.salesTargetOptions.length - 1 : currentBandIndex;
    }
    return 0;
  }
}
